import {ModeSwitch} from '../ModeSwitch';
import ColorModeContext from '@/contexts/colorModeContext';
import {useGetUserTypeQuery} from '@/generated/graphql';
import {useClientType} from '@/hooks/useClientType/useClientType';
import SidebarItem from '@/layout/OldSideBar/SidebarItem';
import {useApolloClient} from '@apollo/client';
import {
  AccountCircle,
  Campaign,
  Groups,
  InsertDriveFile,
  Logout,
  MailRounded,
  MenuBook,
  Policy,
  Settings,
} from '@mui/icons-material';
import {FormControlLabel, useMediaQuery} from '@mui/material';
import Box from '@mui/material/Box';
import {useTheme} from '@mui/styles';
import {userContext} from '@ark/web';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import ReactGA from 'react-ga4';
import {useLocation, useNavigate} from 'react-router-dom';
import {cachePersistor} from '../../main';
import {LogoutConfirmationModal} from './LogoutConfirmation';
import {useBrand} from '@/contexts/BrandContext';

interface SideBarContentProps {
  onSelect: () => void;
}

const ProfileMenuContent = (props: SideBarContentProps) => {
  const theme = useTheme();
  const {name: brandName, supportEmail, supportContactName} = useBrand();
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const client = useApolloClient();

  const {signOut} = React.useContext(userContext);
  const {data} = useGetUserTypeQuery();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const clientType = useClientType();
  const isAdmin = data?.getUser?.clientAdmin;
  const isBTL = clientType === 'BTL';
  const isEnterprise = clientType === 'ENT';
  const [openConfirmLogoutModal, setOpenConfirmLogoutModal] = useState(false);
  const {enqueueSnackbar} = useSnackbar();
  return (
    <Box minWidth="124px">
      <Box sx={{p: isMobile ? 0 : '0 6px'}}>
        {(data?.getUser?.clientAdmin ||
          data?.getUser?.communityManager ||
          data?.getUser?.manager ||
          data?.getUser?.concierge) && (
          <SidebarItem
            selected={pathname.startsWith('/profile')}
            label="Profile"
            icon={<AccountCircle fontSize="small" />}
            handleClick={() => {
              props.onSelect();
              navigate('/profile');
            }}
          />
        )}
        {isAdmin && (
          <SidebarItem
            selected={pathname.startsWith('/members')}
            label="Members"
            icon={<Groups fontSize="small" />}
            handleClick={() => {
              props.onSelect();
              navigate('/members');
            }}
          />
        )}
        {isAdmin && (
          <SidebarItem
            selected={pathname.startsWith('/settings')}
            label="Settings"
            icon={<Settings fontSize="small" />}
            handleClick={() => {
              props.onSelect();
              navigate('/settings');
            }}
          />
        )}
        {isBTL && (
          <>
            <SidebarItem
              selected={false}
              label="Privacy Policy"
              icon={<Policy fontSize="small" />}
              handleClick={() => {
                props.onSelect();
                window.open(
                  'https://ark-public.s3.eu-west-1.amazonaws.com/Ark+-+Website+and+App+Privacy+Policy.pdf',
                );
              }}
            />
            <SidebarItem
              selected={false}
              label="Terms of Service"
              icon={<InsertDriveFile fontSize="small" />}
              handleClick={() => {
                props.onSelect();
                window.open(
                  'https://ark-public.s3.eu-west-1.amazonaws.com/Ark+-+BTL+Terms+%26+Conditions+July+2022.pdf',
                );
              }}
            />
          </>
        )}
        <SidebarItem
          selected={false}
          label="Support"
          icon={<MailRounded fontSize="small" />}
          handleClick={() => {
            props.onSelect();
            window.open(
              `mailto:${supportEmail}?subject=I'd like some help&body=Hi ${supportContactName},...`,
            );
          }}
        />
        {isEnterprise && (
          <SidebarItem
            selected={false}
            label="User Guide"
            icon={<MenuBook fontSize="small" />}
            handleClick={() => {
              props.onSelect();
              window.open(
                'https://ark-rent.notion.site/ark-rent/Building-Better-Communities-User-Guide-4de3b241d35d45a2ba534d58ec49bd15',
              );
              ReactGA.event({
                category: 'Help request',
                action: 'Clicked support button',
              });
            }}
          />
        )}
        <SidebarItem
          selected={false}
          label="Log Out"
          icon={<Logout fontSize="small" />}
          handleClick={async () => {
            setOpenConfirmLogoutModal(true);
          }}
        />
        <SidebarItem
          selected={false}
          label="Features"
          icon={<Campaign />}
          handleClick={() => {
            props.onSelect();
            navigate('/features');
          }}
        />
        <Box alignContent="center" justifyContent="center" ml={2}>
          <ColorModeContext.Consumer>
            {({toggleColorMode}) => (
              <FormControlLabel
                onChange={toggleColorMode}
                control={
                  <ModeSwitch
                    sx={{m: 1}}
                    checked={theme.palette.mode === 'dark' ? true : false}
                  />
                }
                label=""
                labelPlacement="start"
              />
            )}
          </ColorModeContext.Consumer>
        </Box>
      </Box>
      <LogoutConfirmationModal
        modalOpen={openConfirmLogoutModal}
        setModalOpen={setOpenConfirmLogoutModal}
        title="Logout"
        description={`Are you sure you want to logout of ${brandName}?`}
        onConfirm={async () => {
          let cleanupFailed = false;
          try {
            await cachePersistor.purge();
          } catch (error) {
            cleanupFailed = true;
          }
          try {
            await client.clearStore();
          } catch (error) {
            cleanupFailed = true;
          }
          if (cleanupFailed) {
            enqueueSnackbar('Error logging out, please try again', {
              variant: 'error',
            });
          }
          signOut();
          setOpenConfirmLogoutModal(false);
        }}
      />
    </Box>
  );
};

export default ProfileMenuContent;
